import React, { useEffect, useState } from "react"
import { SimpleForm, 
    ReferenceInput,
    SaveButton, 
    DeleteButton,
    required,
    useDataProvider } from "react-admin"
import { useController } from "react-hook-form"
import { ScreenEmpty } from "components/ui/screen"
import { useTranslate } from "ra-core"
import { Grid } from "@mui/material"
import { default as DDTProperties } from "./form.ddt"
import { DeviceKindEntity } from "api/device-kind/device.kind.pb"
import { TextInputWithAudit, AutoSelectSearchReferenceInputWithAudit, AutocompleteInputWithAudit, SelectInputWithAudit } from "components/audit/audit.inputs"

interface props {
    readonly: boolean
    newRecord: boolean
}
const TableName = "devices"

function DDTPropertiesWrapper(props: {readonly: boolean, newRecord: boolean}) {
    const { field: { value: selectedEquipmentP } } = useController({ name: 'equipment_ids[0]' })
     const { field: { value: selectedEquipmentT } } = useController({ name: 'equipment_ids[1]' })

    if (!selectedEquipmentP || !selectedEquipmentT) {
        return <></>
    }
    return <DDTProperties {...props} equipment_ids={[selectedEquipmentP, selectedEquipmentT]} />
}

function Form(props: React.PropsWithChildren<props>): JSX.Element  {
    const { readonly, newRecord } = props
    const translate = useTranslate()
    const dataProvider = useDataProvider()
    const [deviceKinds, setDeviceKinds] = useState<DeviceKindEntity[]>([])

    useEffect(() => {
        dataProvider.getList("device-kinds", {})
            .then(({ data }) => setDeviceKinds(data))
            .catch(() => setDeviceKinds([]))
    }, [dataProvider])

    const getDeviceKindVarnameById = (id: string): string => {
        const kind = deviceKinds.find(k => k.id === id)
        return kind?.varname || ""
    }

    const EquipmentsSelect = (): JSX.Element => {
        const { field: { value: selectedDeviceKind } } = useController({ name: 'device_kind_id' })
        const deviceKindVarname = getDeviceKindVarnameById(selectedDeviceKind)

        if (!selectedDeviceKind) {
            return <></>
        }

        return (
            <>
                <Grid container columnSpacing="8">
                    <Grid item xs={4}>
                        <AutoSelectSearchReferenceInputWithAudit
                            tableName={TableName}
                            disabled={readonly}
                            showAuditIcon={readonly}
                            label={translate("resources.devices.fields.equipment_ids")}
                            source="equipment_ids[0]" 
                            reference="equipments/device-kind"
                            validate={[required()]} 
                            filter={ 
                                deviceKindVarname === 'ddt' 
                                    ? {device_kind_id: selectedDeviceKind, equipment_kind: "sensor_p"}
                                    : {device_kind_id: selectedDeviceKind}
                            } 
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextInputWithAudit 
                            tableName={TableName} 
                            source="equipment_serial_numbers[0]" 
                            showAuditIcon={readonly}
                            disabled={readonly} 
                            validate={[required()]} 
                            label={translate("resources.devices.fields.equipment_serial_numbers")}/>
                    </Grid>
                </Grid>
                <Grid container columnSpacing="8">
                    <Grid item xs={4}>
                        <AutoSelectSearchReferenceInputWithAudit
                            tableName={TableName}
                            disabled={readonly}
                            showAuditIcon={readonly}
                            label={translate("resources.devices.fields.equipment_ids")}
                            source="equipment_ids[1]" 
                            reference="equipments/device-kind"
                            validate={[required()]} 
                            filter={ 
                                deviceKindVarname === 'ddt' 
                                    ? {device_kind_id: selectedDeviceKind, equipment_kind: "sensor_t"}
                                    : {device_kind_id: selectedDeviceKind}
                            } 
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextInputWithAudit tableName={TableName} showAuditIcon={readonly} source="equipment_serial_numbers[1]" disabled={readonly} validate={[required()]} label={translate("resources.devices.fields.equipment_serial_numbers")}/>
                    </Grid>
                </Grid>
            </>
        )
    }

    return (
        <SimpleForm toolbar={false}>
            <Grid container columnSpacing="8">
                <Grid item xs={4}>
                    <TextInputWithAudit tableName={TableName}  showAuditIcon={readonly} source="imei" disabled={readonly} validate={[required()]} label={translate("resources.devices.fields.imei")}/>
                </Grid>
                <Grid item xs={4}>
                    <SelectInputWithAudit
                        tableName={TableName}
                        showAuditIcon={readonly}
                        label={translate("resources.devices.fields.device_kind_id")} 
                        source="device_kind_id" 
                        choices={deviceKinds}
                        optionText="name"
                        optionValue="id"
                        disabled={readonly} 
                        validate={[required()]}
                    />
                </Grid>
            </Grid>
            <EquipmentsSelect />
            <Grid container columnSpacing="8">
                <Grid item xs={4}>
                    <ReferenceInput
                        source="equipment_ids[2]" 
                        reference="equipments"
                        filter={ {equipment_kind: "telemetry"}}>
                        <SelectInputWithAudit source="equipment_ids[2]" showAuditIcon={readonly} tableName={TableName} disabled={readonly} optionText="name" label={translate("resources.devices.fields.equipment_id_bbt")}  validate={[required()]}/>
                    </ReferenceInput>
                </Grid>
                <Grid item xs={4}>
                    <TextInputWithAudit tableName={TableName} showAuditIcon={readonly} source="equipment_serial_numbers[2]" disabled={readonly} validate={[required()]} label={translate("resources.devices.fields.equipment_serial_number_bbt")}/>
                </Grid>
            </Grid>
            <ScreenEmpty enabled={readonly}>
                <Grid container columnSpacing="8">
                    <Grid item xs={4}>
                        <ReferenceInput source="energy_kind_id" reference="energy-kinds">
                            <SelectInputWithAudit tableName={TableName} showAuditIcon={readonly} source="energy_kind_id" label={translate("resources.devices.fields.energy_kind_id")} disabled optionText="name" />
                        </ReferenceInput>
                    </Grid>
                    <Grid item xs={4}>
                        <ReferenceInput source="equipment_driver_id" reference="equipment-drivers">
                            <SelectInputWithAudit tableName={TableName} showAuditIcon={readonly} source="equipment_driver_id" label={translate("resources.equipments.fields.equipment_driver_id")} disabled optionText="name" />
                        </ReferenceInput>
                    </Grid>
                </Grid>
            </ScreenEmpty>
            <Grid container columnSpacing="8">
                <Grid item xs={4}>
                    <ReferenceInput  
                        label={translate("resources.devices.fields.object_id")} 
                        source="object_id" 
                        reference="objects"
                        filterToQuery={(searchText: string) => ({ name: searchText })}>
                        <AutocompleteInputWithAudit tableName={TableName} showAuditIcon={readonly} source="object_id" label={translate("resources.devices.fields.object_id")} disabled={readonly} optionText="name" />
                    </ReferenceInput>
                </Grid>
            </Grid>
            <Grid container>
                <Grid item xs={6}>
                    <TextInputWithAudit tableName={TableName} showAuditIcon={readonly} source="description" disabled={readonly} multiline rows={5} label={translate("resources.common.fields.description")}/>
                </Grid>
            </Grid>
            <DDTPropertiesWrapper readonly={readonly} newRecord={newRecord} />
            <ScreenEmpty enabled={!readonly}>
                <Grid container>
                    <Grid item xs={6}>
                        <SaveButton />
                    </Grid>
                    <ScreenEmpty enabled={!newRecord}>
                        <Grid item xs={6}>
                            <DeleteButton />
                        </Grid>
                    </ScreenEmpty>
                </Grid>
            </ScreenEmpty>
        </SimpleForm>
    )
}

Form.defaultProps = {
    readonly: false,
    newRecord: false,
}

export default Form